import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { formatDate } from 'utils/format';
import { PriceRequest } from 'models/price-request';
import PriceRequestStatusChip from './price-request-status-chip.component';
import { counterpartOf } from './price-request-format';

type PriceRequestLogTableProps = {
    requests: PriceRequest[];
    total: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
};

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

/** DD-3697: the request log — every request, rejected and cancelled ones included, newest first. */
const PriceRequestLogTable: FC<PriceRequestLogTableProps> = ({
    requests,
    total,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const { t: translate } = useTranslation('price-requests');
    const navigate = useNavigate();
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

    return (
        <TableContainer component={Paper}>
            <Table size={isLargeScreen ? 'medium' : 'small'} data-test="price-requests.table">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('NUMBER')}</TableCell>
                        <TableCell>{translate('COUNTERPART')}</TableCell>
                        <TableCell>{translate('OPENED_BY')}</TableCell>
                        <TableCell>{translate('SUMMARY')}</TableCell>
                        <TableCell>{translate('UPDATED')}</TableCell>
                        <TableCell align="right">{translate('STATUS')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {requests.map((request) => (
                        <TableRow
                            key={request._id}
                            hover
                            tabIndex={0}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/price-requests/${request._id}`)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') navigate(`/price-requests/${request._id}`);
                            }}
                            data-test={`price-requests.row-${request._id}`}
                        >
                            <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                    {request.number}
                                </Typography>
                            </TableCell>
                            <TableCell>{counterpartOf(request).name || '–'}</TableCell>
                            <TableCell>
                                <Typography variant="body2">
                                    {translate(`OPENED_BY_${request.initiatorSide}`)}
                                </Typography>
                                {request.createdByName ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {request.createdByName}
                                    </Typography>
                                ) : undefined}
                            </TableCell>
                            <TableCell>{translate('SUMMARY_TEXT', request.summary)}</TableCell>
                            <TableCell>{formatDate(request.updatedAt, 'date')}</TableCell>
                            <TableCell align="right">
                                <PriceRequestStatusChip status={request.status} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_event, newPage) => onPageChange(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
        </TableContainer>
    );
};

export default PriceRequestLogTable;
